<template>
    <div v-bind="$attrs">
        <apex-charts type="bar" :height="height" :width="width" :options="chartOptions" :series="series"></apex-charts>

        <slot name="content"></slot>
    </div>
</template>

<script>
import { i18n, tb } from '@/i18n';
import { colors } from '@/Composables/ChartColors';
import { formatIntegerPrice } from '@/Mixins/Formatting';

export default {
    props: {
        options: {
            type: Object,
            default: function () {
                return {};
            },
        },

        height: {
            type: String,
            default: '100%',
        },

        width: {
            type: String,
            default: '100%',
        },

        title: {
            type: String,
            default: '',
        },

        subtitle: {
            type: String,
        },

        toolbar: {
            type: Boolean,
            default: false,
        },

        hideLegend: {
            type: Boolean,
            default: false,
        },

        showDataLabels: {
            type: Boolean,
            default: false,
        },

        distributed: {
            type: Boolean,
            default: false,
        },

        isPriceChart: {
            type: Boolean,
            default: false,
        },

        barHeight: {
            type: String,
            default: '70%',
        },

        labels: {
            type: Array,
            default: function () {
                return [];
            },
        },

        categories: {
            type: Array,
            default: function () {
                return [];
            },
        },

        annotations: {
            type: Array,
            default: function () {
                return [];
            },
        },

        series: {
            type: Array,
            required: true,
        },

        horizontal: {
            type: Boolean,
            default: false,
        },
    },

    data: function () {
        return {};
    },

    computed: {
        chartOptions() {
            return {
                noData: {
                    text: i18n.t('charts.noData'),
                    align: 'center',
                    verticalAlign: 'middle',
                    offsetX: 0,
                    offsetY: 0,
                    style: {
                        color: undefined,
                        fontSize: '14px',
                        fontFamily: undefined,
                    },
                },
                colors: colors,
                chart: {
                    type: 'bar',
                    height: 350,
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '55%',
                        endingShape: 'rounded',
                    },
                },
                dataLabels: {
                    enabled: this.showDataLabels,
                    background: {
                        enabled: true,
                        opacity: 0.85,
                        foreColor: '#000',
                        dropShadow: {
                            enabled: true,
                            color: '#000',
                            opacity: 0.45,
                        },
                    },
                    formatter: (val) => {
                        return this.isPriceChart ? formatIntegerPrice(val) : val;
                    },
                },
                stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent'],
                },
                xaxis: {
                    categories: (this.categories || []).map((category) => tb(category)),
                },
                yaxis: {
                    labels: {
                        // Will affect the values on the left side of the chart, and also the values on each label's tooltip if tooltip.y.formatter is not used
                        formatter: (val) => {
                            return this.isPriceChart ? formatIntegerPrice(val) : val;
                        },
                    },
                },
                fill: {
                    opacity: 1,
                },
                title: {
                    text: this.title,
                    offsetX: -10,
                    style: {
                        fontSize: '16px',
                        fontWeight: 'bold',
                        fontFamily: 'Spartan',
                        color: '#006DB6',
                        //color: '#154579',
                    },
                },
                subtitle: {
                    offsetX: -10,
                    text: this.subtitle,
                    style: {
                        fontSize: '12px',
                        fontWeight: 'book',
                        fontFamily: 'Spartan',
                        color: '#747B86',
                    },
                },
                legend: {
                    show: !this.hideLegend,
                },
                ...this.options,
                tooltip: {
                    y: {
                        formatter: (val) => {
                            return this.isPriceChart ? formatIntegerPrice(val) : val;
                        },
                    },
                },
            };
        },
    },
};
</script>

<style></style>
